import { RatingChip } from '@/components/ui/rating-chip';
import { AreaMatrix } from '../../area-matrix';
import { matrixColumns, notAssessedAreas, type ReportModel } from '../report-model';
import { PageMissing, PageNote } from './frame';

/**
 * The report's fit-matrix page (T-0283; E24-design §4, §5): the Shortlist
 * step's `AreaMatrix` (T-0276) over the areas frozen with the draft, with
 * the legend above it and the formula note the server wrote under it. The
 * columns are the vendors assessed in depth (shortlisted and alternates,
 * at most eight), from the frozen `area_matrix` table or, for a draft
 * prepared before it, from the frozen fit and the dispositions.
 */
export function ReportAreaMatrix({ model }: { model: ReportModel }) {
  const areas = model.fit.areas ?? [];
  const columns = matrixColumns(model);
  if (areas.length === 0 || columns.length === 0) {
    return (
      <PageMissing>No area ratings were frozen with this draft. Prepare it again.</PageMissing>
    );
  }
  const frozen = model.tables?.area_matrix ?? null;
  const note =
    frozen?.note ??
    `Index = weighted points ÷ total weight (${frozen?.total_weight ?? model.fit.total_weight}), with Strong 1, Partial 0.5, Gap and Unknown 0. Computed by code from the confirmed module ratings, rolled up to the client’s areas.`;
  const leftOut = (frozen?.left_out ?? []).map((p) => p.name).join(', ');
  return (
    <>
      <p className="m-0 flex flex-wrap items-center gap-x-3 gap-y-1 text-meta text-ink-700">
        <span className="inline-flex items-center gap-1">
          <RatingChip rating="strong" state="confirmed" size="sm" /> addresses the area&rsquo;s
          critical needs
        </span>
        <span className="inline-flex items-center gap-1">
          <RatingChip rating="partial" state="confirmed" size="sm" /> evidenced but limited or
          generic
        </span>
        <span className="inline-flex items-center gap-1">
          <RatingChip rating="gap" state="confirmed" size="sm" /> a confirmed limitation
        </span>
        <span className="inline-flex items-center gap-1">
          <RatingChip rating="unknown" state="confirmed" size="sm" /> nothing in the knowledge base
          yet
        </span>
      </p>
      <AreaMatrix
        areas={areas}
        columns={columns}
        notAssessed={notAssessedAreas(model)}
        label="Fit matrix by client area"
      />
      <PageNote>{note}</PageNote>
      {leftOut ? (
        <PageNote>Also assessed in depth, beyond the eighth column: {leftOut}.</PageNote>
      ) : null}
    </>
  );
}
